/*
打った文字と、お題の読みを突き合わせる。

前は許容される綴りを**全部作ってから**含まれるか見ていた。単語なら通る
(長くても数千通り) が、文になると破綻する。ローマ字の綴りは かな 1 つに
つき 2〜8 通りあるので、40 かなの文では組み合わせが兆に届く。
「じ」を打つのに zi と ji のどちらでもよい、という自由度の代償。

なので展開しない。**かなの区切りごとに、今どこまで進めているかの集合**
だけを持って前に進む。長さに比例した手間で済む。
*/

/** かな 1 つぶんの綴り候補を並べたもの。convertToRomajiPatterns の出力 */
export type RomajiSegments = readonly (readonly string[])[];

export interface RomajiMatch {
  /** 打ち間違いなく途中まで来ているか。打ち切っていれば true のまま */
  valid: boolean;
  /** 打ち切ったか */
  complete: boolean;
  /**
   * 画面に出す綴り。**打った綴りに合わせて変える。**
   *
   * si と打った人に shi を見せ続けると、合っているのに赤く見える
   */
  display: string;
}

/**
 * 何も打っていないときの綴り。各区切りの第 1 候補をつなぐ。
 *
 * 表の並び順がそのままヒントになるので、自然な綴りを先頭に置いておく
 */
export function naturalRomaji(segments: RomajiSegments): string {
  let out = "";
  for (const seg of segments) out += seg[0] ?? "";
  return out;
}

/**
 * その区切りで、入力をちょうど食べ切れる候補のうちいちばん長いもの。
 *
 * 長いほうから見るのが要る。「ん」で n と nn の両方が当たるとき、
 * 短い n を取ると次の区切りとずれて、合っているのに赤くなる
 */
function longestExact(
  seg: readonly string[],
  input: string,
  offset: number,
): string | undefined {
  let best: string | undefined;
  for (const option of seg) {
    if (!input.startsWith(option, offset)) continue;
    if (!best || option.length > best.length) best = option;
  }
  return best;
}

export function matchRomaji(
  segments: RomajiSegments,
  input: string,
): RomajiMatch {
  /*
  reach[i] は「区切り i の直前に立てる入力位置」の集合。

  集合で持つのは、同じ入力から進み方が枝分かれするため。
  「んな」を nna と打つと、n を「ん」に当てて na で「な」に行く道と、
  nn を「ん」に当てて a で行き止まる道が同時に立つ
  */
  const reach: Set<number>[] = [new Set([0])];
  for (let i = 0; i < segments.length; i++) {
    const next = new Set<number>();
    for (const offset of reach[i]) {
      for (const option of segments[i]) {
        if (input.startsWith(option, offset)) next.add(offset + option.length);
      }
    }
    reach.push(next);
    if (next.size === 0) break;
  }

  const complete = reach[segments.length]?.has(input.length) ?? false;

  // 打ち切っていなくても、どれかの道の途中に居られれば打ち間違いではない
  let valid = complete;
  if (!valid) {
    outer: for (let i = 0; i < segments.length && i < reach.length; i++) {
      for (const offset of reach[i]) {
        if (offset > input.length) continue;
        const rest = input.slice(offset);
        for (const option of segments[i]) {
          if (option.startsWith(rest)) {
            valid = true;
            break outer;
          }
        }
      }
    }
  }

  return { valid, complete, display: displayFor(segments, input) };
}

/*
出す綴りを決める。

打ったところは打ったとおりに、まだのところは第 1 候補で埋める。
打っている途中の区切りは、その入力から伸ばせる候補を選ぶ。
si まで打った人には shi ではなく si を見せる
*/
function displayFor(segments: RomajiSegments, input: string): string {
  let out = "";
  let offset = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const exact = longestExact(seg, input, offset);
    if (exact !== undefined && offset + exact.length <= input.length) {
      out += exact;
      offset += exact.length;
      continue;
    }
    const rest = input.slice(offset);
    const partial = rest
      ? seg.find((option) => option.startsWith(rest))
      : undefined;
    out += partial ?? seg[0] ?? "";
    for (let j = i + 1; j < segments.length; j++) out += segments[j][0] ?? "";
    return out;
  }
  return out;
}
